import React, { useState, useEffect } from "react";
import { Outlet, Link } from "react-router-dom";
import logoImage from "./logo-png.png";

// Fish Tank Chip Component
const FishTankChip = ({ chip, index }) => {
  const isHeart = index % 3 === 0;
  const chipStyle = {
    position: "absolute",
    left: `${chip.x}px`,
    top: `${chip.y}px`,
    transform: `rotate(${chip.rotation}deg) scale(${chip.scale})`,
    opacity: chip.opacity,
    transition: "all 0.1s ease-out",
  };

  return (
    <div style={chipStyle} className="pointer-events-none">
      {isHeart ? (
        <div className="w-8 h-8 flex items-center justify-center text-2xl animate-pulse">
          💜
        </div>
      ) : (
        <div className="w-10 h-10 rounded-full border-4 border-yellow-400 bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
          💰
        </div>
      )}
    </div>
  );
};

const Layout = () => {
  const [chipPositions, setChipPositions] = useState([]);
  const [chipVelocities, setChipVelocities] = useState([]);
  const [chipsGoingCrazy, setChipsGoingCrazy] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Check if popup should show (first visit)
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem("ferdakids-popup-seen");
    if (!hasSeenPopup) {
      setShowPopup(true);
    }
  }, []);

  // Initialize physics-based background chips
  useEffect(() => {
    const initialChips = [];
    const initialVelocities = [];

    for (let i = 0; i < 50; i++) {
      initialChips.push({
        x: Math.random() * (window.innerWidth - 40),
        y: Math.random() * (window.innerHeight - 40),
        rotation: Math.random() * 360,
        scale: 0.8 + Math.random() * 0.4,
        opacity: 0.3 + Math.random() * 0.4,
      });

      initialVelocities.push({
        x: (Math.random() - 0.5) * 2,
        y: (Math.random() - 0.5) * 2,
        rotation: (Math.random() - 0.5) * 4,
        scale: (Math.random() - 0.5) * 0.02,
        opacity: (Math.random() - 0.5) * 0.02,
      });
    }

    setChipPositions(initialChips);
    setChipVelocities(initialVelocities);
  }, []);

  // Physics animation loop
  useEffect(() => {
    const interval = setInterval(() => {
      setChipPositions((prevPositions) => {
        const newPositions = [...prevPositions];
        setChipVelocities((prevVelocities) => {
          const newVelocities = [...prevVelocities];

          for (let i = 0; i < newPositions.length; i++) {
            const chip = newPositions[i];
            const velocity = newVelocities[i];

            // Add crazy effect when donating
            if (chipsGoingCrazy) {
              velocity.x += (Math.random() - 0.5) * 0.5;
              velocity.y += (Math.random() - 0.5) * 0.5;
            }

            // Update position
            chip.x += velocity.x;
            chip.y += velocity.y;
            chip.rotation += velocity.rotation;
            chip.scale += velocity.scale;
            chip.opacity += velocity.opacity;

            // Bounce off walls
            if (chip.x <= 0 || chip.x >= window.innerWidth - 40) {
              velocity.x *= -0.8;
              chip.x = Math.max(0, Math.min(window.innerWidth - 40, chip.x));
            }
            if (chip.y <= 0 || chip.y >= window.innerHeight - 40) {
              velocity.y *= -0.8;
              chip.y = Math.max(0, Math.min(window.innerHeight - 40, chip.y));
            }

            // Elastic collisions between chips
            for (let j = i + 1; j < newPositions.length; j++) {
              const otherChip = newPositions[j];
              const otherVelocity = newVelocities[j];
              const dx = chip.x - otherChip.x;
              const dy = chip.y - otherChip.y;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance < 50) {
                const angle = Math.atan2(dy, dx);
                const targetX = chip.x + Math.cos(angle) * 50;
                const targetY = chip.y + Math.sin(angle) * 50;

                velocity.x += (targetX - chip.x) * 0.01;
                velocity.y += (targetY - chip.y) * 0.01;
                otherVelocity.x -= (targetX - chip.x) * 0.01;
                otherVelocity.y -= (targetY - chip.y) * 0.01;
              }
            }

            // Constrain scale and opacity
            chip.scale = Math.max(0.5, Math.min(1.5, chip.scale));
            chip.opacity = Math.max(0.2, Math.min(0.8, chip.opacity));
          }

          return newVelocities;
        });
        return newPositions;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [chipsGoingCrazy]);

  const closePopup = () => {
    setShowPopup(false);
    localStorage.setItem("ferdakids-popup-seen", "true");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-blue-800 rounded-2xl p-8 max-w-md w-full border-2 border-yellow-400 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-4">🎰</div>
              <h2 className="text-2xl font-bold text-white mb-4">
                Welcome to Ferda Kids!
              </h2>
              <div className="text-gray-200 space-y-3 text-left">
                <p>
                  For inquiries about this site, or if you'd like me to make you
                  your own site, please DM me on Instagram at{" "}
                  <span className="text-yellow-400 font-semibold">
                    @tqrqn
                  </span>
                  .
                </p>
                <p className="text-sm text-gray-300">
                  Note: none of the other contact links on this site are active.
                  This is the only way to reach me.
                </p>
              </div>
              <button
                onClick={closePopup}
                className="mt-6 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold py-3 px-8 rounded-full hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Got it! 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fish tank physics background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {chipPositions.map((chip, index) => (
          <FishTankChip key={index} chip={chip} index={index} />
        ))}
      </div>

      {/* Header with Logo */}
      <header className="text-center mb-0 z-10 relative">
        <Link
          to="/"
          className="inline-block hover:scale-105 transition-transform duration-200"
        >
          <img
            src={logoImage}
            alt="Ferda Kids Logo"
            className="mx-auto h-48 w-auto object-contain filter drop-shadow-lg"
          />
        </Link>
      </header>

      {/* Navigation Links */}
      <div className="flex justify-center gap-8 -mt-6 mb-12 z-10 relative">
        <Link
          to="/lives-changed"
          className="text-gray-300 hover:text-green-400 transition-colors duration-200"
        >
          <div className="flex items-center space-x-2">
            <span className="text-lg">💚</span>
            <span className="text-sm font-medium">Kids We Helped</span>
          </div>
        </Link>

        <Link
          to="/max-wins"
          className="text-gray-300 hover:text-yellow-400 transition-colors duration-200"
        >
          <div className="flex items-center space-x-2">
            <span className="text-lg">🎰</span>
            <span className="text-sm font-medium">Togi's Max Wins</span>
          </div>
        </Link>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center relative z-10 w-full min-h-0 px-4">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-16 mb-8 text-center z-10 relative">
        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-gray-600/30">
          <p className="text-gray-400 text-xs mb-2">
            By using this site, you agree to our terms and privacy policy.
          </p>
          <div className="flex justify-center space-x-4 text-xs">
            <a
              href="/privacy"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-gray-500">•</span>
            <a
              href="/terms"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              Terms of Service
            </a>
            <span className="text-gray-500">•</span>
            <a
              href="/contact"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              Contact Us
            </a>
          </div>
          <p className="text-gray-500 text-xs mt-2">
            © 2025 Togi's Max Wins Ferda Kids. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
